import PropTypes from 'prop-types'
import React from 'react'
import range from 'lodash/range'
import { Link } from 'react-router-dom'
import Helmet from 'react-helmet'

import { parseLocation, makeLink } from '../../../common/utils/urlHelper'
import RightOpenIcon from '../icons/rightOpen'
import LeftOpenIcon from '../icons/leftOpen'

/**
 * Return array of pages by given params with gap elements if needed. For pagination purpose. Examples:
 *  getPagesRange(1, 20, 10) -> [1, 2, 3, 4, 5, 6, 7, 8, 9, '...', 20]
 *  getPagesRange(11, 20, 10) -> [1, '...', 7, 8, 9, 10, 11, 12, 13, 14, '...', 20]
 * @param {Number} currentPage
 * @param {Number} pagesCount
 * @param {Number} maxDisplayedPages
 * @returns {Array}
 */
const getPagesRange = (currentPage, pagesCount, maxDisplayedPages) => {
  if (pagesCount <= maxDisplayedPages) {
    return range(1, pagesCount + 1)
  }

  let pagesRange = [currentPage]
  let minPage = currentPage - 1
  let maxPage = currentPage + 1
  let direction = 1

  while (pagesRange.length < maxDisplayedPages) {
    if (direction < 0 && minPage > 0) {
      pagesRange.unshift(minPage)
      minPage--
    }

    if (direction > 0 && maxPage <= pagesCount) {
      pagesRange.push(maxPage)
      maxPage++
    }

    direction *= -1
  }

  if (pagesRange[0] !== 1) {
    pagesRange.splice(0, 1, 1, '...')
  }

  if (pagesRange[pagesRange.length - 1] !== pagesCount) {
    pagesRange.pop()
    pagesRange.push('...', pagesCount)
  }

  return pagesRange
}

const scrollToTop = () => {
  window.scrollTo(0, 0)
}

const PaginationItem = ({ value, link, isCurrentPage }) => {
  let content

  if (value === '...') {
    content = <span className="pagination__gap">{value}</span>
  } else if (isCurrentPage) {
    content = <span className="pagination__page--current">{value}</span>
  } else {
    content = (
      <Link to={link} className="pagination__page" onClick={scrollToTop}>
        {value}
      </Link>
    )
  }
  return <li>{content}</li>
}

const Pagination = ({
  currentPage = 1,
  currentSite,
  totalItemsCount,
  pageSize,
  location,
  maxDisplayedPages = 10,
  baseUrl,
  maxPagesCount
}) => {
  const calculatedPagesCount = Math.ceil(totalItemsCount / pageSize)
  const pagesCount =
    maxPagesCount && calculatedPagesCount > maxPagesCount
      ? maxPagesCount
      : calculatedPagesCount
  const parsedLocation = parseLocation(location)
  const previousPageLink =
    currentPage > 1
      ? makeLink({ ...parsedLocation, page: currentPage - 1 }, currentSite)
      : null
  const nextPageLink =
    currentPage < pagesCount
      ? makeLink({ ...parsedLocation, page: currentPage + 1 }, currentSite)
      : null
  const shouldShowPagination = pagesCount > 1

  const link = []
  if (previousPageLink) {
    link.push({
      rel: 'prev',
      href: baseUrl + previousPageLink
    })
  }
  if (nextPageLink) {
    link.push({
      rel: 'next',
      href: baseUrl + nextPageLink
    })
  }

  return (
    <div>
      {shouldShowPagination && (
        <nav className="pagination">
          <Helmet link={link} />
          <ul className="pagination__list">
            {previousPageLink ? (
              <li>
                <Link
                  to={previousPageLink}
                  className="pagination__previous"
                  onClick={scrollToTop}
                >
                  <LeftOpenIcon />
                </Link>
              </li>
            ) : null}
            {getPagesRange(currentPage, pagesCount, maxDisplayedPages).map(
              (pageNumber, index) => {
                const pageLink = makeLink(
                  { ...parsedLocation, page: pageNumber },
                  currentSite
                )
                return (
                  <PaginationItem
                    key={index}
                    value={pageNumber}
                    link={pageLink}
                    isCurrentPage={currentPage === pageNumber}
                  />
                )
              }
            )}
            {nextPageLink ? (
              <li>
                <Link
                  to={nextPageLink}
                  className="pagination__next"
                  onClick={scrollToTop}
                >
                  <RightOpenIcon />
                </Link>
              </li>
            ) : null}
          </ul>
        </nav>
      )}
    </div>
  )
}

Pagination.propTypes = {
  currentPage: PropTypes.number,
  currentSite: PropTypes.string,
  totalItemsCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  location: PropTypes.object.isRequired,
  maxDisplayedPages: PropTypes.number,
  maxPagesCount: PropTypes.number
}

export default Pagination



// WEBPACK FOOTER //
// ./client/components/pagination/pagination.js